/** @jsxImportSource theme-ui */
import {Box, Button, Input, Link, Text} from "theme-ui";
import Icon from "./common/Icon";
import StarRating from "./StarRating";
import {ImageRequest} from "../types/imagerequest";
import apiInvocation from "../api/apiInvocation";
import {useState} from "react";
import PhotoModal from "./PhotoModal";
import StaticStarRating from "./StaticStarRating";
import CustomSelect from "./CustomSelect";
import dayjs from "dayjs";
import EndangeredStatusToggle from "./EndangeredStatusToggle";

interface SingleResultProps {
    result: ImageRequest | undefined;
    isAdmin?: boolean;
    onDeleteSuccess?: (deletedId: string) => void;
}

const SingleResult = ({result, isAdmin, onDeleteSuccess}: SingleResultProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [overridenName, setOverridenName] = useState("");
    const [overridenRating, setOverridenRating] = useState<number>(0);
    const [insertedName, setInsertedName] = useState("");
    const [hasEnlistedEndangeredSpecies, setHasEnlistedEndangeredSpecies] = useState<boolean | null>(null);

    /**
     * Handles the submission of the input field
     */
    const handleInputSubmit = async () => {
        const trimmedName = insertedName.trim();
        if (trimmedName === "") return;

        const matchedSpecies = Object.keys(interestingSpeciesTypes).find(
            species => species.toLowerCase() === trimmedName.toLowerCase()
        );

        if (matchedSpecies) {
            // Acts as if selected from dropdown
            await handleOverridenName(matchedSpecies);
            setInsertedName("");
            return;
        }

        // Otherwise treat it as a manual custom name
        console.log("Submitting freeform name:", trimmedName);
        setOverridenName(trimmedName);

        try {
            if (result?.id !== undefined) {
                await apiInvocation.overrideName(result.id, trimmedName, false);
                setInsertedName("");
                setHasEnlistedEndangeredSpecies(false);
            }
        } catch (error) {
            console.error("Error setting overridden name or rating:", error);
        }
    };
    
    const [isEndangered, setIsEndangered] = useState(result?.aiResponse?.isEndangered ?? false);
    const [isInvasive, setIsInvasive] = useState(result?.aiResponse?.isInvasive ?? false);

    const handleStatusChange = (newStatus: boolean) => {
        // This callback receives only `isEndangered` as true/false
        // We infer `isInvasive` based on the state rotation
        if (isEndangered) {
            // was endangered → next is invasive
            setIsEndangered(false);
            setIsInvasive(true);
        } else if (isInvasive) {
            // was invasive → next is none
            setIsInvasive(false);
        } else {
            // was none → next is endangered
            setIsEndangered(true);
        }
    };


    const interestingSpeciesTypes: Record<string, { rating: number; status: 'ENDANGERED' | 'INVASIVE' }> = {
        // ENDANGERED
        "Black Rail": {rating: 10, status: "ENDANGERED"},
        "Golden-cheeked Warbler": {rating: 10, status: "ENDANGERED"},
        "Whooping Crane": {rating: 10, status: "ENDANGERED"},
        "Brazos Water Snake": {rating: 10, status: "ENDANGERED"},
        "Texas Horned Lizard": {rating: 10, status: "ENDANGERED"},
        "Texas Heelsplitter": {rating: 10, status: "ENDANGERED"},
        "Texas Pigtoe": {rating: 10, status: "ENDANGERED"},
        "Sandbank Pocketbook": {rating: 10, status: "ENDANGERED"},
        "Texas Fawnsfoot": {rating: 10, status: "ENDANGERED"},
        "Glen Rose Yucca": {rating: 10, status: "ENDANGERED"},
        "Strecker's Chorus Frog": {rating: 7, status: "ENDANGERED"},
        "Chestnut-collared Longspur": {rating: 7, status: "ENDANGERED"},
        "Sprague's Pipit": {rating: 7, status: "ENDANGERED"},
        "Black-capped Vireo": {rating: 7, status: "ENDANGERED"},
        "Bald Eagle": {rating: 7, status: "ENDANGERED"},
        "Western Burrowing Owl": {rating: 7, status: "ENDANGERED"},
        "Mountain Plover": {rating: 7, status: "ENDANGERED"},
        "American Bumblebee": {rating: 7, status: "ENDANGERED"},
        "Eastern Spotted Skunk": {rating: 7, status: "ENDANGERED"},
        "Tricolored Bat": {rating: 7, status: "ENDANGERED"},
        "American Black Bear": {rating: 7, status: "ENDANGERED"},
        "Hoary Bat": {rating: 7, status: "ENDANGERED"},
        "Green Hawthorn": {rating: 7, status: "ENDANGERED"},
        "Reverchon Prairie-clover": {rating: 7, status: "ENDANGERED"},
        "Glandular Gayfeather": {rating: 7, status: "ENDANGERED"},
        "Hall's Prairie-clover": {rating: 7, status: "ENDANGERED"},
        "Denseflower False Foxglove": {rating: 7, status: "ENDANGERED"},
        "Reverchon's Scurfpea": {rating: 7, status: "ENDANGERED"},
        "Shinners' Sedge": {rating: 7, status: "ENDANGERED"},
        "Reflexed Milkvetch": {rating: 7, status: "ENDANGERED"},
        "Topeka Purple Coneflower": {rating: 7, status: "ENDANGERED"},
        "Lifting Dodder": {rating: 7, status: "ENDANGERED"},
        "Alligator Snapping Turtle": {rating: 7, status: "ENDANGERED"},
        "Eastern Box Turtle": {rating: 7, status: "ENDANGERED"},
        "Ornate Box Turtle": {rating: 7, status: "ENDANGERED"},
        "Northern Prairie Skink": {rating: 7, status: "ENDANGERED"},
        "Slender Glass Lizard": {rating: 7, status: "ENDANGERED"},
        "Smooth Softshell Turtle": {rating: 7, status: "ENDANGERED"},
        "Eastern Garter Snake": {rating: 7, status: "ENDANGERED"},
        "Western Massasauga": {rating: 7, status: "ENDANGERED"},
        "Big Brown Bat": {rating: 3, status: "ENDANGERED"},
        "Big Free-tailed Bat": {rating: 3, status: "ENDANGERED"},
        "Black-tailed Prairie Dog": {rating: 3, status: "ENDANGERED"},
        "Cave Myotis": {rating: 3, status: "ENDANGERED"},
        "Eastern Red Bat": {rating: 3, status: "ENDANGERED"},
        "Long-tailed Weasel": {rating: 3, status: "ENDANGERED"},
        "Mountain Lion": {rating: 3, status: "ENDANGERED"},
        "Muskrat": {rating: 3, status: "ENDANGERED"},
        "Swamp Rabbit": {rating: 3, status: "ENDANGERED"},
        "White-backed Hog-nosed Skunk": {rating: 3, status: "ENDANGERED"},
        "Timber Rattlesnake": {rating: 3, status: "ENDANGERED"},
        "Western Chicken Turtle": {rating: 3, status: "ENDANGERED"},
        "Strecker's Mussel": {rating: 3, status: "ENDANGERED"},

        // INVASIVE
        "Chinese Privet": {rating: 3, status: "INVASIVE"},
        "Nandina": {rating: 3, status: "INVASIVE"},
        "Japanese Honeysuckle": {rating: 3, status: "INVASIVE"},
        "Chinaberry Tree": {rating: 3, status: "INVASIVE"},
        "Tallow Tree": {rating: 3, status: "INVASIVE"},
        "Johnson Grass": {rating: 3, status: "INVASIVE"},
        "Giant Reed": {rating: 3, status: "INVASIVE"},
        "Tree of Heaven": {rating: 3, status: "INVASIVE"},
        "Elephant Ear": {rating: 3, status: "INVASIVE"},
        "Callery Pear": {rating: 3, status: "INVASIVE"},
        "English Ivy": {rating: 3, status: "INVASIVE"},
        "Vitex": {rating: 3, status: "INVASIVE"},
        "Salt Cedar": {rating: 3, status: "INVASIVE"},
        "Brazilian Peppertree": {rating: 3, status: "INVASIVE"},
        "Mimosa Tree": {rating: 3, status: "INVASIVE"},
        "Coral vine": {rating: 3, status: "INVASIVE"},
        "Alligatorweed": {rating: 3, status: "INVASIVE"},
        "Paper mulberry": {rating: 3, status: "INVASIVE"},
        "Slender-flowered thistle": {rating: 3, status: "INVASIVE"},
        "Nodding plumeless thistle": {rating: 3, status: "INVASIVE"},
        "Buchkiller": {rating: 3, status: "INVASIVE"},
        "Camphor tree": {rating: 3, status: "INVASIVE"},
        "Japanese dodder": {rating: 3, status: "INVASIVE"},
        "Hydrilla": {rating: 3, status: "INVASIVE"},
        "Swamp morning-glory": {rating: 3, status: "INVASIVE"},
        "Limnophila": {rating: 3, status: "INVASIVE"},
        "Purple loosestrife": {rating: 3, status: "INVASIVE"},
        "Melaleuca": {rating: 3, status: "INVASIVE"},
        "Torpedograss": {rating: 3, status: "INVASIVE"},
        "Feral Hog": {rating: 3, status: "INVASIVE"},
        "Nutria": {rating: 3, status: "INVASIVE"},
        "Zebra Mussel": {rating: 3, status: "INVASIVE"},
        "Emerald Ash Borer": {rating: 3, status: "INVASIVE"},
        "Red Ambrosia Beetle": {rating: 3, status: "INVASIVE"},
        "Red Imported Fire Ant": {rating: 3, status: "INVASIVE"},
        "Cactus Moth": {rating: 3, status: "INVASIVE"},
        "Asian Longhorned Beetle": {rating: 3, status: "INVASIVE"},
        "Mediterranean House Gecko": {rating: 3, status: "INVASIVE"},
        "Cuban Tree Frog": {rating: 3, status: "INVASIVE"},
        "Tilapia": {rating: 3, status: "INVASIVE"},
        "Grass Carp": {rating: 3, status: "INVASIVE"},
        "Silver Carp": {rating: 3, status: "INVASIVE"},
        "Bighead Carp": {rating: 3, status: "INVASIVE"},
        "Black Carp": {rating: 3, status: "INVASIVE"},
        "Australian red claw crayfish": {rating: 3, status: "INVASIVE"},
        "Brown Tree Snake": {rating: 3, status: "INVASIVE"},
        "European Starling": {rating: 3, status: "INVASIVE"},
        "Eurasian Collared Dove": {rating: 3, status: "INVASIVE"},
        "Rock Pigeon": {rating: 3, status: "INVASIVE"},
        "Giant African Snail": {rating: 3, status: "INVASIVE"},
        "Apple Snail": {rating: 3, status: "INVASIVE"},
        "Quilted Melania": {rating: 3, status: "INVASIVE"},
    };

    /**
     * Get rating by name.
     * @param name The common name to fetch the rating for.
     * @returns The rating or null if not found.
     */
    const getRatingByName = (name: string): number => {
        return interestingSpeciesTypes[name]?.rating ?? 0;
    };

    const handleDelete = async () => {
        if (result?.id) {
            try {
                await apiInvocation.deleteRequest(result.id);
                // Call the callback to remove the item from the parent component's state
                if (onDeleteSuccess) {
                    onDeleteSuccess(result.id);
                }
            } catch (error) {
                console.error("Error while deleting image request", error);
            }
        }
    }

    const handleRate = async (rating: number) => {
        if (result?.id) {
            try {
                await apiInvocation.setRating(result.id, rating, true);
                console.log("Rating successfully set:", rating);
            } catch (error) {
                console.error("Error setting rating:", error);
            }
        }
    };

    /**
     * This method is setting new overriden name and then by overriden name
     * we set proper rating for it
     * @param name selected from list of endangered/invasive species
     */
    const handleOverridenName = async (name: string) => {
        setOverridenName(name);

        try {
            if (result?.id !== undefined) {
                const speciesInfo = interestingSpeciesTypes[name];

                if (!speciesInfo) {
                    console.warn("Selected species not found in dictionary.");
                    return;
                }


                const newRating = speciesInfo.rating;
                if (speciesInfo.status === "ENDANGERED") {
                    await apiInvocation.overrideName(result.id, name, true);

                    setOverridenRating(newRating);
                    handleRate(newRating);
                    setHasEnlistedEndangeredSpecies(true);
                    setIsEndangered(true);
                    setIsInvasive(false);
                } else {
                    await apiInvocation.overrideNameInvasive(result.id, name, false);
                    setOverridenRating(newRating);
                    handleRate(newRating);
                    setHasEnlistedEndangeredSpecies(false);
                    setIsEndangered(false);
                    setIsInvasive(true);
                }
            }
        } catch (error) {
            console.error("Error setting overridden name or rating:", error);
        }
    };

    const isOverridenName = (overridenName !== "" && overridenName !== result?.aiResponse?.name) 
        || (result?.aiResponse?.overridenName && result?.aiResponse?.overridenName !== result?.aiResponse?.name);

    /**
     * We decide which name to use, overriden or name from api response
     */
    const getDisplayName = (): string => {
        if (overridenName) {
            return overridenName;
        }
        if (result?.aiResponse?.overridenName) {
            return result.aiResponse.overridenName;
        }
        return result?.aiResponse?.name || "Unknown";
    };


    const imageUrl = `${process.env?.REACT_APP_IMAGES_BASE_URL}/thumbnails_${result?.id}`;
    return (
        <Box
            variant="cards.primary"
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                mt: 13,
                pb: [10, 11],
                pt: 0,
                px: [9, 12],
                position: "relative",
            }}
        >

            <Button
                onClick={handleDelete}
                sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    color: "#dc3545",
                    border: "1px solid rgba(220, 53, 69, 0.3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    //zIndex: 10,
                    transition: "all 0.2s ease",
                    "&:hover": {
                        backgroundColor: "#dc3545",
                        color: "white",
                        transform: "scale(1.1)",
                    },
                }}
            >
                <Icon name="bin" size={16} />
            </Button>
            <Box
                sx={{
                    background: "background",
                    position: "relative",
                    top: "-40px",
                    width: "120px",
                    height: "120px",
                    borderRadius: 8,
                    outline: "solid",
                    outlineWidth: "12px",
                    outlineColor: "background",
                    overflow: "hidden",
                }}
                onClick={() => setIsModalOpen(true)}
            >

                <img
                    src={imageUrl}
                    alt="Plant"
                    style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                    }}
                />
            </Box>


            {(!result?.aiResponse?.isAnimalRecognized && !result?.aiResponse?.isPlantRecognized) && (
                <Box
                    sx={{
                        display: "flex",
                        position: "relative",
                        top: "-58px",
                        width: "40px",
                        height: "40px",
                        background: "warning",
                        borderRadius: 8,
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Icon name="info" color="buttonText" size={24}/>
                </Box>
            )}

            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                gap: 4,
                minWidth: '200px',
                justifyContent: 'center',
                mb: 4,
                mt: (result?.aiResponse?.isAnimalRecognized || result?.aiResponse?.isPlantRecognized) ? "-2px" : "-48px",
            }}>
                <Text
                    sx={{
                        fontSize: [4, 6],
                        fontWeight: "medium",
                        textAlign: 'center',
                        flex: 1,
                    }}
                >
                    {(result?.aiResponse?.isAnimalRecognized || result?.aiResponse?.isPlantRecognized || isOverridenName)
                        ? getDisplayName()
                        : "We couldn't identify this plant or animal"}
                </Text>

                <Box sx={{ width: '40px', display: 'flex', justifyContent: 'center' }}>
                  <CustomSelect
                    options={Object.keys(interestingSpeciesTypes).sort((a, b) => a.localeCompare(b))}
                    onSelect={handleOverridenName}
                  />
                </Box>
            </Box>

            {(result?.aiResponse?.isAnimalRecognized || result?.aiResponse?.isPlantRecognized) && (
                <Text
                    sx={{
                        fontSize: [1, 3],
                        mt: [1, 2],
                    }}
                >
                    {isOverridenName ? "" : result.aiResponse?.latinName}
                </Text>
            )}

            {((isOverridenName && result) || result?.aiResponse?.isAnimalRecognized || result?.aiResponse?.isPlantRecognized) && (
                <EndangeredStatusToggle
                    isAdmin={true}
                    id={result.id}
                    isInvasive={isInvasive}
                    isEndangered={isEndangered}
                    onStatusChange={handleStatusChange}
                    hasEnlistedOnEndangeredSpecies={result?.aiResponse?.hasEnlistedEndangeredSpacies ?? false}
                    isOnPBVList={hasEnlistedEndangeredSpecies}
                />
            )}

            <Box sx={{
                display: 'flex',
                justifyContent: result?.creaetdByUser && (result.biodiversitySite || (result.latitude !== 0 || result.longitude !== 0)) ? 'space-between' : 'center',
                flexDirection: ['column', 'row'],
                width: '100%',
                alignItems: 'center',
                mb: 4,
                px: 4,
                gap: 4
            }}>
                <Text
                    sx={{
                        fontSize: [0, 2],
                        mt: [1, 2],
                        mb: [2, 3],
                        color: "secondaryText",
                    }}
                >
                    Photo is taken by: {result?.creaetdByUser}
                </Text>
                {result?.biodiversitySite ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 4,
                            mt: [2, 3],
                            mb: [3, 4],
                        }}
                    >
                        <Icon name="compass" color="secondaryText" size={12}/>
                        <Text
                            sx={{
                                fontSize: [0, 2],
                                color: "secondaryText",
                            }}
                        >
                            {result.biodiversitySite}
                        </Text>
                    </Box>
                ) : (result?.latitude != null && result?.longitude != null && (result.latitude !== 0 || result.longitude !== 0)) ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 4,
                            mt: [2, 3],
                            mb: [3, 4],
                        }}
                    >
                        <Icon name="compass" color="secondaryText" size={12}/>
                        <Link
                            href={`https://www.google.com/maps?q=${result.latitude},${result.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                fontSize: [0, 2],
                                color: "secondaryText",
                                textDecoration: "underline",
                                cursor: "pointer",
                                "&:hover": { color: "primary" },
                            }}
                        >
                            {`${result.latitude.toFixed(4)}, ${result.longitude.toFixed(4)}`}
                        </Link>
                    </Box>
                ) : null}
            </Box>

            <Text
                sx={{
                    fontSize: [1, 3],
                    color: "secondaryText",
                    mb: [4, 8],
                    mt: (result?.aiResponse?.isAnimalRecognized || result?.aiResponse?.isPlantRecognized) ? 0 : 2,
                }}
            >
              {isOverridenName ? "AI description overriden" : 
                (result?.aiResponse?.isAnimalRecognized || result?.aiResponse?.isPlantRecognized)
                  ? result?.aiResponse?.description
                  : "Some plants and animals are harder to identify. Try a clearer image."
              }
            </Text>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 3,
                    width: ["100%", "60%"], // Full width on mobile, 60% width on PC
                    maxWidth: "400px", // Prevents excessive width on large screens
                    mt: 4,
                    mb: 4,
                }}
            >
                <Input
                    sx={{
                        flex: 1,
                        height: ["35px", "40px"], // Bigger height: 44px for mobile, 50px for PC
                        padding: 3,
                        borderRadius: 6,
                        border: "1px solid #ccc", // Light gray border
                        fontSize: [2, 3], // Slightly larger text size
                        "&:focus": {
                            outline: "none",
                            borderColor: "primaryButton",
                        },
                    }}
                    placeholder="Enter new name"
                    value={insertedName}
                    onChange={(e: any) => setInsertedName(e.target.value)}
                />
                <Button
                    sx={{
                        variant: "buttons.primary",
                        fontSize: [2, 3],
                        px: 4,
                        py: 4,
                        height: ["35px", "40px"]
                    }}
                    onClick={handleInputSubmit}
                >
                    Save
                    {/* <Icon
                name="floppy-disk"
                color="buttonText"
                size={20}

              /> */}
                </Button>
            </Box>


            {isAdmin ? (<StarRating onRate={handleRate}
                                    currentRating={overridenRating !== 0 ? overridenRating : result?.rating ?? 0}/>) :
                (<StaticStarRating currentRating={result?.rating ?? 0}/>)
            }
            {(result?.ratedByUser && isAdmin) && (
                <Text
                    sx={{
                        fontSize: [1, 3],
                        mt: [1, 2],
                        mb: [2, 3],
                        color: 'secondaryText'
                    }}
                >
                    Photo is rated by: {result?.ratedByUser}
                </Text>
            )}
            <Text
                sx={{
                    fontSize: [1, 3],
                    mt: [1, 2],
                    mb: [2, 3],
                    color: 'secondaryText'
                }}
            >
                {result?.createdAt ? dayjs(result.createdAt).format('MMMM D, YYYY') : 'N/A'}
            </Text>

            <PhotoModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                imageUrl={imageUrl.replace("thumbnails_", "")} // Replace with the full image URL
            />
        </Box>
    );
};

export default SingleResult;